import { eq } from '@art-suite/art-core-ts';
import { Scope } from '../OptimizedValueExpressions';
import { pureFunctionalMap } from '../pureFunctionalLib';
import { getIdsByDepth, SCAN_CHILDREN_FIELD_NAMES_BY_DEPTH } from '../ScanRoot';
import { ScanTemplateRoot } from '../ScanTemplateRoot';
import { getOptimizedValueExpressions, ScanPartOptimized, ScanRootOptimized } from '.';
import { getOptimizedScanPart } from './getOptimizedScan';

const EMPTY_SCOPE: Scope = {};

/**
 * Recursively transforms a scan part and its children using a custom transform function.
 *
 * The transform is applied to the current node first, then recursively to children.
 * This is a pure functional transform - it only creates new objects when changes occur.
 *
 * @param scanTemplateRoot - The scan template root for optimization
 * @param scanPart - The scan part to transform
 * @param transform - Custom transform function applied to each node
 * @param depth - Current depth in the tree
 * @param idsByDepth - Array tracking IDs at each depth level
 * @param parentScope - Scope from parent for value expression resolution
 * @returns The transformed scan part (same object if no changes)
 */
const transformScanPartOptimizedRecursive = (
  scanTemplateRoot: ScanTemplateRoot,
  scanPart: ScanPartOptimized,
  transform: (scanPart: ScanPartOptimized, depth: number, idsByDepth: (string | undefined)[], parentScope: Scope) => ScanPartOptimized,
  depth: number,
  idsByDepth: (string | undefined)[],
  parentScope: Scope,
): ScanPartOptimized => {
  const scanPartWithUpdatedScope = eq(parentScope, scanPart.optimized.optimizedValueExpressions.scope) ? scanPart : {
    ...scanPart,
    optimized: {
      ...scanPart.optimized,
      optimizedValueExpressions: getOptimizedValueExpressions(scanPart.template, parentScope, scanPart, scanPart.optimized.optimizedValueExpressions),
    },
  };

  // Apply the custom transform first
  let transformedScanPart = transform(scanPartWithUpdatedScope, depth, idsByDepth, parentScope);

  // Get the field name for children at this depth
  const childrenFieldName = SCAN_CHILDREN_FIELD_NAMES_BY_DEPTH[depth];
  const children = (transformedScanPart as any)[childrenFieldName] as ScanPartOptimized[] | undefined;
  let transformedChildren = children;

  // If no children possible at this depth, return the transformed part
  if (childrenFieldName && children && children.length > 0) {
    // Get scope for children from the transformed scan part
    const scopeForChildren = transformedScanPart.optimized.optimizedValueExpressions.resolved;

    // Recursively transform children
    transformedChildren = pureFunctionalMap(
      children,
      child => {
        idsByDepth[depth + 1] = child.id;
        const result = transformScanPartOptimizedRecursive(
          scanTemplateRoot,
          child,
          transform,
          depth + 1,
          idsByDepth,
          scopeForChildren,
        );
        idsByDepth[depth + 1] = undefined;
        return result;
      },
    );
  }

  // If nothing changed (neither the node nor its children nor the scope), return original
  if (transformedScanPart === scanPart && transformedChildren === children) {
    return scanPart;
  }

  // If the scanPart or children changed, reoptimize
  const transformedScanPartWithUpdatedChildren = transformedChildren !== children
    ? { ...transformedScanPart, [childrenFieldName]: transformedChildren }
    : transformedScanPart;

  // Use getOptimizedScanPart to recompute all optimizations (aggregations, scope, etc.)
  // Pass the original scanPart as the "before" state for optimization reuse
  return getOptimizedScanPart(
    scanTemplateRoot,
    transformedScanPart.template,
    transformedScanPartWithUpdatedChildren,
    depth,
    idsByDepth,
    parentScope,
    scanPart,
  );
};

/**
 * Transforms an entire scan root tree using a custom transform function.
 *
 * The transform function is applied to each scan part in the tree, starting with
 * the current node before recursing to children. This maintains a pure functional
 * approach - only creating new objects when actual changes occur.
 *
 * Scope data flows down from parent to children, and aggregated values (reduced values,
 * photo IDs, search text) flow back up from children to parents via getOptimizedScanPart.
 *
 * @param scanRoot - The scan root to transform
 * @param transform - Function that takes a scan part and depth, returns transformed scan part
 * @returns Transformed scan root (same object if no changes)
 *
 * @example
 * const withUpdatedNotes = transformScanRootOptimized(scanRoot, (scanPart, depth) => {
 *   if (scanPart.id === targetId) {
 *     return { ...scanPart, notes: "Updated notes" };
 *   }
 *   return scanPart;
 * });
 */
export const transformScanRootOptimized = (
  scanRoot: ScanRootOptimized,
  transform: (scanPart: ScanPartOptimized, depth: number, idsByDepth: (string | undefined)[], parentScope: Scope) => ScanPartOptimized,
): ScanRootOptimized =>
  transformScanPartOptimizedRecursive(
    scanRoot.template,
    scanRoot,
    transform,
    0,
    getIdsByDepth(),
    EMPTY_SCOPE,
  ) as ScanRootOptimized;
